import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { ownerApi, type OwnerAuditEntry } from '@/features/workspace/api';
import { useToast } from '@/hooks/useToast';
import { routes } from '@/lib/routes';
import '@/features/orders-publications/business-operations.css';

type AuditRow = OwnerAuditEntry;

function formatAuditAt(value: string): string {
  try {
    return new Date(value).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

/**
 * Port of template `presovet/block7-potok01-owner-business.js` → auditView.
 */
export const OwnerSystemAuditView = () => {
  const navigate = useNavigate();
  const { notify } = useToast();
  const [auditRows, setAuditRows] = useState<AuditRow[]>([]);
  const [filters, setFilters] = useState({
    role: 'Все',
    course: 'Все',
    group: 'Все',
    date: '',
    user: '',
    type: 'Все',
    module: 'Все',
    project: '',
  });

  useEffect(() => {
    let cancelled = false;
    ownerApi
      .listAudit()
      .then((rows) => {
        if (!cancelled) setAuditRows(rows);
      })
      .catch(() => {
        if (!cancelled) {
          setAuditRows([]);
          notify('Не удалось загрузить журнал аудита');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const roles = useMemo(
    () => ['Все', ...Array.from(new Set(auditRows.map((x) => x.role)))],
    [auditRows],
  );
  const courses = useMemo(
    () => ['Все', ...Array.from(new Set(auditRows.map((x) => x.course).filter((v) => v && v !== '—')))],
    [auditRows],
  );
  const groups = useMemo(
    () => ['Все', ...Array.from(new Set(auditRows.map((x) => x.group).filter((v) => v && v !== '—')))],
    [auditRows],
  );
  const types = useMemo(
    () => ['Все', ...Array.from(new Set(auditRows.map((x) => x.type)))],
    [auditRows],
  );
  const modules = useMemo(
    () => ['Все', ...Array.from(new Set(auditRows.map((x) => x.module)))],
    [auditRows],
  );

  const rows = auditRows.filter((x) => {
    if (filters.role !== 'Все' && x.role !== filters.role) return false;
    if (filters.course !== 'Все' && x.course !== filters.course) return false;
    if (filters.group !== 'Все' && (x.group || '—') !== filters.group) return false;
    if (filters.date && !String(x.at).startsWith(filters.date)) return false;
    if (filters.user && !`${x.user} ${x.role}`.toLowerCase().includes(filters.user.toLowerCase())) {
      return false;
    }
    if (filters.type !== 'Все' && x.type !== filters.type) return false;
    if (filters.module !== 'Все' && x.module !== filters.module) return false;
    if (
      filters.project &&
      !String(x.project ?? '').toLowerCase().includes(filters.project.toLowerCase())
    ) {
      return false;
    }
    return true;
  });

  const surveyCount = auditRows.filter((x) => /SURVEY_/.test(x.type)).length;
  const accountCount = auditRows.filter((x) => /ACCOUNT|ROLE|CAPABILITY/.test(x.type)).length;
  const contentCount = auditRows.filter((x) => /CONTENT/.test(x.type)).length;

  const set = (key: keyof typeof filters, value: string) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="biz-ops-overlay open" role="dialog" aria-modal="true" aria-label="История системы">
      <div className="biz-ops-top">
        <button type="button" className="biz-ops-btn" onClick={() => navigate(routes.owner)}>
          ← Назад
        </button>
        <div>
          <h2>История системы</h2>
          <p>Журнал действий без раскрытия личных блокнотов и закрытых файлов</p>
        </div>
        <div className="biz-ops-top-actions">
          <button type="button" className="biz-ops-btn" onClick={() => navigate(routes.owner)}>
            Закрыть
          </button>
        </div>
      </div>

      <main className="biz-ops-main">
        <div className="biz-ops-wrap">
          <div className="biz-ops-crumb">
            Бизнес-операции / <b>История системы</b>
          </div>

          <section className="biz-ops-panel">
            <h3>Журнал без личных данных</h3>
            <p>
              Журнал показывает действие, роль, объект, модуль и разрешённые метаданные. Содержимое
              личных блокнотов, рукописей и закрытых файлов не раскрывается без отдельного разрешения.
            </p>
            <div className="biz-ops-audit-filters">
              <select className="biz-ops-select" value={filters.role} onChange={(e) => set('role', e.target.value)}>
                {roles.map((v) => (
                  <option key={v}>{v}</option>
                ))}
              </select>
              <select
                className="biz-ops-select"
                value={filters.course}
                onChange={(e) => set('course', e.target.value)}
              >
                {courses.map((v) => (
                  <option key={v}>{v}</option>
                ))}
              </select>
              <select
                className="biz-ops-select"
                value={filters.group}
                onChange={(e) => set('group', e.target.value)}
              >
                {groups.map((v) => (
                  <option key={v}>{v}</option>
                ))}
              </select>
              <input
                className="biz-ops-input"
                type="date"
                value={filters.date}
                onChange={(e) => set('date', e.target.value)}
              />
              <input
                className="biz-ops-input"
                placeholder="Пользователь / идентификатор"
                value={filters.user}
                onChange={(e) => set('user', e.target.value)}
              />
              <select className="biz-ops-select" value={filters.type} onChange={(e) => set('type', e.target.value)}>
                {types.map((v) => (
                  <option key={v}>{v}</option>
                ))}
              </select>
              <select
                className="biz-ops-select"
                value={filters.module}
                onChange={(e) => set('module', e.target.value)}
              >
                {modules.map((v) => (
                  <option key={v}>{v}</option>
                ))}
              </select>
              <input
                className="biz-ops-input"
                placeholder="Проект / опрос / заказ"
                value={filters.project}
                onChange={(e) => set('project', e.target.value)}
              />
            </div>
          </section>

          <div className="biz-ops-kpis">
            <div className="biz-ops-kpi">
              <b>{rows.length}</b>
              <span>после фильтра</span>
            </div>
            <div className="biz-ops-kpi">
              <b>{surveyCount}</b>
              <span>опросы</span>
            </div>
            <div className="biz-ops-kpi">
              <b>{accountCount}</b>
              <span>аккаунты / роли</span>
            </div>
            <div className="biz-ops-kpi">
              <b>{contentCount}</b>
              <span>контент</span>
            </div>
            <div className="biz-ops-kpi">
              <b>Доступ</b>
              <span>защита личных данных</span>
            </div>
          </div>

          <div className="biz-ops-audit-list">
            {rows.map((x) => (
              <div key={x.id} className="biz-ops-audit-row" style={{ '--accent': '#9e80ff' } as CSSProperties}>
                <div>{formatAuditAt(x.at)}</div>
                <div className="actor">
                  {x.role} · {x.user}
                  <br />
                  <small>
                    {x.course || '—'} · {x.group || '—'}
                  </small>
                </div>
                <div className="event">{x.type}</div>
                <div className="detail">
                  {x.module} · {x.detail}
                  {x.project && x.project !== '—' ? ` · ${x.project}` : ''}
                </div>
                <div className="privacy">{x.privacy}</div>
              </div>
            ))}
            {!auditRows.length ? (
              <div className="biz-ops-notice">Записей в журнале пока нет.</div>
            ) : !rows.length ? (
              <div className="biz-ops-notice">По текущему фильтру записей нет.</div>
            ) : null}
          </div>
        </div>
      </main>
    </div>
  );
};
